"use client"

/**
 * Client-side fetch wrapper that handles rate limiting.
 * Detects 429 responses and triggers rate limit modal.
 * 
 * @param input - The URL or Request object to fetch
 * @param init - Optional fetch options
 * @returns Promise<Response>
 */
export async function fetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  // Check if we're currently rate limited
  const isRateLimited = (): boolean => {
    if (typeof window === "undefined") return false
    
    const rateLimitState = sessionStorage.getItem("rateLimitState")
    if (!rateLimitState) return false
    
    try {
      const { isLimited, expiresAt } = JSON.parse(rateLimitState)
      if (isLimited && expiresAt && Date.now() < expiresAt) {
        return true
      }
      // Clean up expired rate limit
      sessionStorage.removeItem("rateLimitState")
      return false
    } catch {
      return false
    }
  }

  // Block request if rate limited
  if (isRateLimited()) {
    throw new Error("Rate limit active. Please wait before making more requests.")
  }

  // Make the fetch request
  const response = await globalThis.fetch(input, init)

  // Check for rate limiting (429 status or "Too Many Requests")
  if (response.status === 429 || response.statusText?.toLowerCase().includes("too many requests")) {
    // Extract retry-after from header or default to 60 seconds
    const retryAfterHeader = response.headers.get("retry-after") || response.headers.get("Retry-After")
    const retryAfter = retryAfterHeader ? parseInt(retryAfterHeader, 10) : 60

    // Store rate limit state
    if (typeof window !== "undefined") {
      const expiresAt = Date.now() + retryAfter * 1000
      sessionStorage.setItem(
        "rateLimitState",
        JSON.stringify({ isLimited: true, expiresAt })
      )

      // Dispatch event to notify rate limit context
      window.dispatchEvent(
        new CustomEvent("rateLimit", { detail: { isLimited: true, retryAfter } })
      )
    }
  } else {
    // Clear rate limit state on successful non-429 response
    if (typeof window !== "undefined" && response.ok) {
      sessionStorage.removeItem("rateLimitState")
      window.dispatchEvent(
        new CustomEvent("rateLimit", { detail: { isLimited: false, retryAfter: null } })
      )
    }
  }

  return response
}

